import { supabase } from '../config/supabaseClient';
import { getApiBaseUrl } from '../config/apiConfig';
import { isE2eMocksEnabled, readE2eSession } from '../testing/e2eRuntime';

const FALLBACK_HOSTS = [
	'http://localhost:8012',
	'http://127.0.0.1:8012',
	'http://localhost:8000',
	'http://127.0.0.1:8000',
];

let lastSuccessfulBackendBaseUrl = null;

function trimTrailingSlash(value) {
	return String(value || '').replace(/\/+$/, '');
}

function normalizePath(path) {
	const raw = String(path || '');
	return raw.startsWith('/') ? raw : `/${raw}`;
}

function addUniqueCandidate(candidates, url) {
	if (url && !candidates.includes(url)) {
		candidates.push(url);
	}
}

function isLocalhostBaseUrl(url) {
	try {
		const parsed = new URL(url);
		return ['localhost', '127.0.0.1', '::1'].includes(parsed.hostname);
	} catch {
		return false;
	}
}

function isBrowserLocalhost() {
	if (typeof window === 'undefined') return false;
	return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
}

function getPathnameOnly(path) {
	return String(path || '').split(/[?#]/)[0];
}

function deriveBaseUrlFromSuccessfulRequest(url, normalizedPath) {
	const pathOnly = getPathnameOnly(normalizedPath);
	const urlWithoutQuery = String(url || '').split(/[?#]/)[0];
	if (!pathOnly || !urlWithoutQuery.endsWith(pathOnly)) {
		return null;
	}

	const baseUrl = trimTrailingSlash(urlWithoutQuery.slice(0, -pathOnly.length));
	return baseUrl || null;
}

function rememberSuccessfulBackendUrl(url, normalizedPath) {
	const baseUrl = deriveBaseUrlFromSuccessfulRequest(url, normalizedPath);
	if (baseUrl) {
		lastSuccessfulBackendBaseUrl = baseUrl;
	}
}

export async function getBackendAuthHeaders(extraHeaders = {}, { includeApiKey = true, body } = {}) {
	let session = null;
	if (isE2eMocksEnabled()) {
		session = readE2eSession();
	}

	if (!session) {
		const { data } = await supabase.auth.getSession();
		session = data?.session || null;
	}

	if (!session?.access_token) {
		throw new Error('No active session');
	}

	const headers = {
		Authorization: `Bearer ${session.access_token}`,
		...extraHeaders,
	};

	if (includeApiKey) {
		const apiKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
		if (apiKey && !headers.apikey) {
			headers.apikey = apiKey;
		}
	}

	if (body instanceof FormData) {
		delete headers['Content-Type'];
		delete headers['content-type'];
	} else if (body !== undefined && body !== null && !(body instanceof Blob) && !headers['Content-Type'] && !headers['content-type']) {
		headers['Content-Type'] = 'application/json';
	}

	return headers;
}

export function buildBackendCandidates(path) {
	const normalizedPath = normalizePath(path);
	const candidates = [];
	const configuredBaseUrl = trimTrailingSlash(getApiBaseUrl());
	const explicitApiUrl = !!process.env.REACT_APP_API_URL;

	if (lastSuccessfulBackendBaseUrl) {
		addUniqueCandidate(candidates, `${lastSuccessfulBackendBaseUrl}${normalizedPath}`);
	}

	if (!configuredBaseUrl.endsWith('/api/v1')) {
		addUniqueCandidate(candidates, `${configuredBaseUrl}/api/v1${normalizedPath}`);
	}
	addUniqueCandidate(candidates, `${configuredBaseUrl}${normalizedPath}`);

	if (isBrowserLocalhost() && (!explicitApiUrl || isLocalhostBaseUrl(configuredBaseUrl))) {
		FALLBACK_HOSTS.forEach((host) => {
			const trimmedHost = trimTrailingSlash(host);
			if (!normalizedPath.startsWith('/api/v1/')) {
				addUniqueCandidate(candidates, `${trimmedHost}/api/v1${normalizedPath}`);
			}
			addUniqueCandidate(candidates, `${trimmedHost}${normalizedPath}`);
		});
	}

	return candidates;
}

async function parseResponseBody(response, responseType) {
	if (responseType === 'response') {
		return response;
	}

	if (responseType === 'blob') {
		return response.blob();
	}

	if (responseType === 'text') {
		return response.text();
	}

	if (response.status === 204) {
		return null;
	}

	const contentType = response.headers.get('content-type') || '';
	if (contentType.includes('application/json')) {
		return response.json();
	}

	const text = await response.text();
	if (!text) {
		return null;
	}

	try {
		return JSON.parse(text);
	} catch {
		return text;
	}
}

async function parseErrorBody(response) {
	const contentType = response.headers.get('content-type') || '';
	if (contentType.includes('application/json')) {
		return response.json().catch(() => ({}));
	}

	const text = await response.text().catch(() => '');
	if (!text) {
		return {};
	}

	try {
		return JSON.parse(text);
	} catch {
		return { error: text };
	}
}

function buildBackendErrorMessage(status, errorBody = null) {
	return errorBody?.error || errorBody?.detail || errorBody?.message || `Request failed (${status})`;
}

function shouldRetryBackendResponse(response, errorBody, candidateUrls, currentIndex, retryStatuses) {
	if (retryStatuses.includes(response.status)) {
		return true;
	}

	const hasMoreCandidates = currentIndex < candidateUrls.length - 1;
	const hasBackendErrorPayload = Boolean(errorBody?.error || errorBody?.detail || errorBody?.message);
	return response.status === 403 && hasMoreCandidates && !hasBackendErrorPayload;
}

export async function backendFetch(path, options = {}) {
	const normalizedPath = normalizePath(path);
	const candidateUrls = buildBackendCandidates(normalizedPath);
	const {
		method = 'GET',
		headers: extraHeaders = {},
		body,
		signal,
		responseType = 'json',
		retryStatuses = [404, 502, 503],
		maxCandidates = null,
	} = options;
	const activeCandidateUrls = Number.isInteger(maxCandidates) && maxCandidates > 0
		? candidateUrls.slice(0, maxCandidates)
		: candidateUrls;

	const headers = await getBackendAuthHeaders(extraHeaders, { body });
	const requestInit = {
		method,
		headers,
		body,
		signal,
	};

	if (body && !(body instanceof FormData) && !(body instanceof Blob) && typeof body !== 'string') {
		requestInit.body = JSON.stringify(body);
	}

	let lastError = null;

	for (let index = 0; index < activeCandidateUrls.length; index += 1) {
		const url = activeCandidateUrls[index];
		let response;
		try {
			response = await fetch(url, requestInit);
		} catch (error) {
			lastError = error;
			continue;
		}

		if (response.ok) {
			rememberSuccessfulBackendUrl(url, normalizedPath);
			const data = await parseResponseBody(response, responseType);
			return { data, response, url };
		}

		let errorBody = null;
		try {
			errorBody = await parseErrorBody(response);
		} catch {
			errorBody = null;
		}

		if (shouldRetryBackendResponse(response, errorBody, activeCandidateUrls, index, retryStatuses)) {
			const retryableError = new Error(buildBackendErrorMessage(response.status, errorBody));
			retryableError.status = response.status;
			retryableError.payload = errorBody;
			retryableError.url = url;
			lastError = retryableError;
			continue;
		}

		const message = buildBackendErrorMessage(response.status, errorBody);
		const error = new Error(message);
		error.status = response.status;
		error.payload = errorBody;
		error.url = url;
		throw error;
	}

	if (lastError instanceof TypeError) {
		throw new Error('Cannot connect to backend API. Start q-safe-backend and verify API URL/port (8000 or 8012).');
	}

	throw lastError || new Error('Backend request failed');
}

export async function backendRequest(path, options = {}) {
	const { data } = await backendFetch(path, options);
	return data;
}
